import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import './i18n';
import './App.css';
import './gs-redesign.css';

// Public Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import TechnologyPage from './pages/TechnologyPage';
import CreativeArtPage from './pages/CreativeArtPage';
import TalentPage from './pages/TalentPage';
import VenuesPage from './pages/VenuesPage';
import PortfolioPage from './pages/PortfolioPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import ArtistDetailPage from './pages/ArtistDetailPage';
import VenueDetailPage from './pages/VenueDetailPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import TransportPage from './pages/TransportPage';
import TransportDetailPage from './pages/TransportDetailPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';

// Dashboard Pages — preserve existing protected functionality
import SuperAdminDashboard from './pages/dashboards/SuperAdminDashboard';
import CuratorDashboard from './pages/dashboards/CuratorDashboard';
import ArtistDashboard from './pages/dashboards/ArtistDashboard';
import ModelDashboard from './pages/dashboards/ModelDashboard';
import VenueDashboard from './pages/dashboards/VenueDashboard';
import TransportDashboard from './pages/dashboards/TransportDashboard';

import { ProtectedRoute } from './components/ProtectedRoute';

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* New commercial hierarchy */}
      <Route path="/" element={<HomePage />} />
      <Route path="/technology" element={<TechnologyPage />} />
      <Route path="/creative-art" element={<CreativeArtPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/portfolio/:slug" element={<ProjectDetailPage />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* Existing public functionality — preserved */}
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/services/:slug" element={<ServiceDetailPage />} />
      <Route path="/talent" element={<TalentPage />} />
      <Route path="/talent/:slug" element={<ArtistDetailPage />} />
      <Route path="/venues" element={<VenuesPage />} />
      <Route path="/venues/:slug" element={<VenueDetailPage />} />
      <Route path="/transport" element={<TransportPage />} />
      <Route path="/transport/:slug" element={<TransportDetailPage />} />
      <Route path="/eventos" element={<EventsPage />} />
      <Route path="/eventos/:slug" element={<EventDetailPage />} />
      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
      <Route path="/faq" element={<FAQPage />} />

      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected dashboards — unchanged */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/curator/*"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin', 'curator']}>
            <CuratorDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/artist/*"
        element={
          <ProtectedRoute allowedRoles={['artist', 'admin', 'super_admin']}>
            <ArtistDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/model/*"
        element={
          <ProtectedRoute allowedRoles={['model', 'admin', 'super_admin']}>
            <ModelDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/venue/*"
        element={
          <ProtectedRoute allowedRoles={['venue_owner', 'admin', 'super_admin']}>
            <VenueDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transport-provider/*"
        element={
          <ProtectedRoute allowedRoles={['transport_provider', 'admin', 'super_admin']}>
            <TransportDashboard />
          </ProtectedRoute>
        }
      />

      {/* Legal */}
      <Route path="/privacy" element={<div className="min-h-screen bg-[#050505] pt-32 px-8"><h1 className="text-white text-3xl">Política de Privacidad</h1></div>} />
      <Route path="/terms" element={<div className="min-h-screen bg-[#050505] pt-32 px-8"><h1 className="text-white text-3xl">Términos y Condiciones</h1></div>} />
      <Route path="/cookies" element={<div className="min-h-screen bg-[#050505] pt-32 px-8"><h1 className="text-white text-3xl">Política de Cookies</h1></div>} />

      <Route path="*" element={
        <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="font-display text-6xl text-white mb-4">404</h1>
            <p className="text-white/60 mb-8">Página no encontrada</p>
            <a href="/" className="btn-metal">Volver al Inicio</a>
          </div>
        </div>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
